"use client";

import { Track } from "@/src/lib/tracking";
import React, { useEffect, useRef } from "react";
import { cn } from "utils";
import QRCodeStyling from "qr-code-styling";

export interface StyledQRCodeProps {
  /** 二维码内容 */
  value: string;
  /** 二维码尺寸（像素），默认 240 */
  size?: number;
  /** 边距（模块数），默认 2 */
  margin?: number;
  /** 前景色（二维码点的颜色），默认白色 */
  foregroundColor?: string;
  /** 背景色，默认深灰色 */
  backgroundColor?: string;
  /** 容错等级，默认 M */
  errorCorrectionLevel?: "L" | "M" | "Q" | "H";
  /** 外层容器 className，用于控制圆角/阴影等 */
  className?: string;
}

export const StyledQRCode: React.FC<StyledQRCodeProps> = ({
  value,
  size = 240,
  margin = 2,
  foregroundColor = "#FFFFFF",
  backgroundColor = "#1a1a1a",
  errorCorrectionLevel = "M",
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const qrRef = useRef<any | null>(null);

  useEffect(() => {
    if (!containerRef.current || !value) {
      return;
    }

    try {
      if (!qrRef.current) {
        // 首次创建实例
        // 优化配置以提高扫码成功率和清晰度：
        // 1. 使用更高的容错等级（H = 30%）
        // 2. 简化圆角样式，避免过度变形
        // 3. 确保颜色对比度足够
        // 4. 使用 SVG 渲染提高清晰度（矢量图，自动适应高 DPI）
        qrRef.current = new QRCodeStyling({
          width: size,
          height: size,
          data: value,
          margin: Math.max(margin, 2), // 确保至少 2 个模块的边距
          type: "svg", // 使用 SVG 渲染，更清晰
          qrOptions: {
            errorCorrectionLevel: errorCorrectionLevel || "H", // 默认使用最高容错
          },
          dotsOptions: {
            type: "dots", // 圆角像素，但不过度圆角
            color: foregroundColor,
          },
          backgroundOptions: {
            color: backgroundColor,
          },
          cornersSquareOptions: {
            type: "rounded", // 改为 rounded，避免 extra-rounded 过度变形
            color: foregroundColor,
          },
          cornersDotOptions: {
            type: "dot",
            color: foregroundColor, // 修正：角内小点应该和前景色一致
          },
        });

        // 附加到容器
        qrRef.current.append(containerRef.current);
      } else {
        // 更新已有实例
        qrRef.current.update({
          width: size,
          height: size,
          data: value,
          margin: Math.max(margin, 2),
          type: "svg",
          qrOptions: {
            errorCorrectionLevel: errorCorrectionLevel || "H",
          },
          dotsOptions: {
            color: foregroundColor,
          },
          backgroundOptions: {
            color: backgroundColor,
          },
          cornersSquareOptions: {
            color: foregroundColor,
          },
          cornersDotOptions: {
            color: foregroundColor, // 修正：角内小点应该和前景色一致
          },
        });
      }
    } catch (err) {
      Track.log(
        "error",
        "StyledQRCode",
        "StyledQRCode 使用 qr-code-styling 生成二维码失败:",
        { cause: String(err) },
      );
    }
  }, [
    value,
    size,
    margin,
    foregroundColor,
    backgroundColor,
    errorCorrectionLevel,
  ]);

  return (
    <div
      ref={containerRef}
      className={cn("inline-flex items-center justify-center", className)}
      style={{ width: size, height: size }}
    />
  );
};
